import type { GetLabels200Item } from '@ipfabric/api-client/client/schemas';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { FormMultiSelect } from '@ipfabric/ui-components/FormMultiSelect/FormMultiSelect';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { FloatingList } from '@ipfabric/ui-components/List/FloatingList/FloatingList';
import { type Control, useFieldArray, useWatch } from 'react-hook-form';
import type { DataPolicyFormValues } from './dataPolicyFormSchema';

type DataPolicyConditionEditorProps = {
  control: Control<DataPolicyFormValues>;
  enrolledLabels: readonly GetLabels200Item[];
  disabled?: boolean;
  error?: string;
};

export const DataPolicyConditionEditor = ({
  control,
  enrolledLabels,
  disabled,
  error,
}: DataPolicyConditionEditorProps) => {
  const { fields, append, remove } = useFieldArray({ control, name: 'terms' });
  const terms = useWatch({ control, name: 'terms' }) ?? [];
  const usedLabels = new Set(terms.map((term) => term.label));

  const availableItems = enrolledLabels
    .filter((label) => !usedLabels.has(label.name))
    .map((label) => ({
      label: label.name,
      // Valued labels match on values ("is"); value-less labels match on presence ("is set").
      onClick: () =>
        append({
          label: label.name,
          op: label.values.length > 0 ? 'in' : 'exists',
          values: [],
        }),
    }));

  return (
    <FormField
      description={
        <span className='inline-flex flex-wrap items-center gap-3xs'>
          Use
          <Icon glyph='fa-plus-circle' size='sm' />
          Add condition to specify policy targets.
        </span>
      }
      error={error}
      label='Policy condition'
      link={
        disabled ? undefined : availableItems.length > 0 ? (
          <FloatingList items={availableItems} size='small'>
            {(setRef, setIsOpen, open) => (
              <Button
                dataTestId='data-policy-add-condition'
                forwardRef={setRef}
                icon='fa-plus-circle'
                iconPosition='start'
                onClick={() => setIsOpen(!open)}
                type='button'
                variant='plain'
              >
                Add condition
              </Button>
            )}
          </FloatingList>
        ) : (
          // Every enrolled label is already used (or none is enrolled).
          <Button
            dataTestId='data-policy-add-condition'
            disabled
            icon='fa-plus-circle'
            iconPosition='start'
            type='button'
            variant='plain'
          >
            Add condition
          </Button>
        )
      }
    >
      {fields.length > 0 ? (
        <div className='flex flex-col gap-2xs'>
          {fields.map((field, index) => (
            <ConditionRow
              key={field.id}
              control={control}
              disabled={disabled}
              index={index}
              labelValues={
                enrolledLabels.find((label) => label.name === field.label)
                  ?.values ?? []
              }
              onRemove={() => remove(index)}
            />
          ))}
        </div>
      ) : null}
    </FormField>
  );
};

type ConditionRowProps = {
  control: Control<DataPolicyFormValues>;
  disabled?: boolean;
  index: number;
  labelValues: readonly string[];
  onRemove: () => void;
};

const ConditionRow = ({
  control,
  disabled,
  index,
  labelValues,
  onRemove,
}: ConditionRowProps) => {
  const label = useWatch({ control, name: `terms.${index}.label` });
  const op = useWatch({ control, name: `terms.${index}.op` });

  return (
    <div
      className='flex items-start gap-xs'
      data-testid={`data-policy-condition-${label}`}
    >
      <span className='min-w-[100px] pt-xs text-sm font-medium'>{label}</span>
      <span className='min-w-[48px] pt-xs text-sm text-element-secondary'>
        {op === 'in' ? 'is' : 'is set'}
      </span>
      <div className='flex-1'>
        {op === 'in' && (
          <FormMultiSelect
            control={control}
            disabled={disabled}
            name={`terms.${index}.values`}
            options={labelValues.map((value) => ({ label: value, value }))}
            placeholder='Select values'
          />
        )}
      </div>
      {!disabled && (
        <Button
          accent='negative'
          icon='fa-trash-can'
          onClick={onRemove}
          size='small'
          type='button'
          variant='plain'
        />
      )}
    </div>
  );
};
